<template>
  <v-container fluid justify-start>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Your Invoices'"
      v-bind:icon="'receipt'"
    />

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />
    <v-card v-if="!isLoading">
      <template v-if="invoices.length === 0 && !isLoading">
        <h1 class="font-weight-light">
          You have no invoices
        </h1>
      </template>
      <template v-else>
        <v-card>
          <v-card-text>
            <v-data-iterator
              :items="filteredInvoices"
              :items-per-page.sync="itemsPerPage"
              >
              <template v-slot:header>
                <v-toolbar dark flat :color="appColor_ContentToolbar">
                  <v-toolbar-items class="pt-4">
                    <v-text-field
                      v-model="invNum"
                      type="tel"
                      min="0"
                      max="99999"
                      mask="#####"
                      label="Invoice #"
                      append-icon="search"
                      clearable
                    />
                    <v-select
                      v-model="saleYear"
                      :items="saleYears"
                      label="Sale Year"
                      class="ml-4"
                    />
                  </v-toolbar-items>
                </v-toolbar>
              </template>

              <template v-slot:default="props">
                <v-row>
                  <v-col
                    v-for="item in props.items"
                    :key="item.invnum"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                    >
                    <v-card>
                      <v-toolbar dark flat :color="appColor_ContentToolbarLight">
                        <v-toolbar-title>
                          {{ getTitleText(item) }}
                        </v-toolbar-title>
                      </v-toolbar>

                      <v-divider />

                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>Sale Date:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ getDateFormat(item.springSaleDate) }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>Birth Year:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ item.birthYear }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>Breed:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ item.breed }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider />
                        <v-list-group>
                          <v-list-item slot="activator">
                            <v-list-item-title>Bulls ({{ item.bulls.length }}):</v-list-item-title>
                            <v-list-item-title class="align-end">
                              {{ getMoneyFormat(item.amtBull.toFixed(2)) }}
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            v-for="bull in item.bulls"
                            :key="bull.calfId"
                            >
                            <v-list-item-avatar class="caption">
                              {{ bull.selectionNum }}
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title>{{ bull.calfId }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-content class="align-end pr-3 caption">
                              {{ bull.class }}
                            </v-list-item-content>
                            <v-list-item-action>{{ getMoneyFormat(bull.price.toFixed(2)) }}</v-list-item-action>
                          </v-list-item>
                        </v-list-group>
                        <v-list-item>
                          <v-list-item-content>Trucking:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ getMoneyFormat((item.amtTrucking + item.amtTruckingGst).toFixed(2)) }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>Adjustment:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ getMoneyFormat(item.amtAdjustment.toFixed(2)) }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>Insurance:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ getMoneyFormat(item.amtInsurance.toFixed(2)) }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider />
                        <v-list-item>
                          <v-list-item-content>Credit:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ getMoneyFormat(item.amtCredit.toFixed(2)) }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>Deposit:</v-list-item-content>
                          <v-list-item-content class="align-end">
                            {{ getMoneyFormat(item.amtDeposit.toFixed(2)) }}
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider />
                        <v-list-item>
                          <v-list-item-content>Total:</v-list-item-content>
                          <v-list-item-content class="align-end body-2">
                            <b>{{ getMoneyFormat(item.amtInvoice.toFixed(2)) }}</b>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </v-data-iterator>
          </v-card-text>
        </v-card>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import { mapGetters } from 'vuex'
import { AppColor } from '@/config.js'

export default {
  name: 'BbCustomerInvoices',
  components: {
    AppPageTitle
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Background: AppColor.Background,
      appColor_Progress: AppColor.Progress,
      appColor_ContentToolbar: AppColor.ContentToolbar,
      appColor_ContentToolbarLight: AppColor.ContentToolbarLight,

      isLoading: false,
      invoices: [],
      saleYears: [],
      saleYear: '0',
      invNum: '',
      itemsPerPage: 4
    }
  },
  computed: {
    ...mapGetters(['profile']),
    filteredInvoices () {
      const self = this
      const year = parseInt(self.saleYear)
      const invNum = self.invNum.toString().trim()

      return self.invoices.filter(function (value) {
        if (year > 0 && invNum.length > 0) {
          return value.birthYear + 1 === year && value.invNum.toString().includes(invNum)
        } else if (year > 0) {
          return value.birthYear + 1 === year
        } else if (invNum.length > 0) {
          return value.invNum.toString().includes(invNum)
        } else {
          return self.invoices
        }
      })
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true
      appService.getCustomerInvoices(self.profile.bbCustomerStatus.accountNo).then(data => {
        self.invoices = data.invoices
        self.saleYears = data.saleYears
        self.isLoading = false
      })
    },

    getMoneyFormat (x) {
      if (typeof x === 'undefined') return ''
      return '$ ' + x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    getDateFormat (date) {
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('MMMM DD, YYYY')
        }
      }
      return ''
    },

    getTitleText (inv) {
      if (inv !== null) {
        return 'Invoice #' + inv.invNum
      }
      return ''
    }
  }
}
</script>

